/*
  Live chat: one open stream to the API (Server-Sent Events) that delivers the team's replies,
  closed/reopened markers and "seen" the instant they happen. Uses fetch rather than EventSource so
  the sign-in token travels in a header, never in the address. Reconnects with back-off, refreshes
  the thread on every reconnect so nothing is missed, and reports whether it is connected.
*/
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "app/store";
import { chatApi } from "./chat";
import { API_BASE } from "./index";
import type { ChatMessage } from "./types";
import storage, { STORAGE_KEYS } from "app/utils/storage";

type LiveEvent =
  | { type: "message"; message: ChatMessage }
  | { type: "status"; status: "active" | "closed" }
  | { type: "seen"; upTo: number }
  | { type: "hello"; now: string };

export const liveState = { connected: false };

export function useChatStream(enabled: boolean) {
  const dispatch = useDispatch<AppDispatch>();
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    let controller: AbortController | null = null;
    let attempt = 0;

    const apply = (ev: LiveEvent) => {
      if (ev.type === "hello") return;
      dispatch(chatApi.util.updateQueryData("getChat", undefined, (draft) => {
        if (ev.type === "message") {
          const m = ev.message;
          const byClient = m.clientId ? draft.messages.findIndex((x) => x.clientId === m.clientId) : -1;
          const byId = draft.messages.findIndex((x) => x.id === m.id);
          if (byClient >= 0) draft.messages[byClient] = m;
          else if (byId >= 0) draft.messages[byId] = m;
          else { draft.messages.push(m); draft.messages.sort((a, b) => (a.id > 0 && b.id > 0 ? a.id - b.id : 0)); }
          if (m.kind === "closed") draft.status = "closed";
          if (m.kind === "reopened" || m.kind === "out") draft.status = "active";
        } else if (ev.type === "status") {
          draft.status = ev.status;
        } else if (ev.type === "seen") {
          for (const x of draft.messages) if (x.kind === "in" && x.id > 0 && x.id <= ev.upTo) x.seen = true;
        }
      }));
    };

    const handleChunk = (chunk: string) => {
      const data = chunk.split("\n").filter((l) => l.startsWith("data:")).map((l) => l.slice(5).trim()).join("\n");
      if (!data) return;
      try { apply(JSON.parse(data) as LiveEvent); } catch { /* ignore a malformed frame */ }
    };

    const run = async () => {
      while (!stopped) {
        const token = storage.getItem(STORAGE_KEYS.TOKEN);
        if (!token) return;
        controller = new AbortController();
        try {
          const res = await fetch(`${API_BASE}/api/chat/stream`, { headers: { Authorization: `Bearer ${token}`, Accept: "text/event-stream" }, signal: controller.signal, cache: "no-store" });
          if (res.status === 401) return; // signed out: the session handler takes over
          if (!res.ok || !res.body) throw new Error(`stream ${res.status}`);
          setConnected(true); liveState.connected = true;
          if (attempt > 0) void dispatch(chatApi.endpoints.getChat.initiate(undefined, { forceRefetch: true })); // catch up after a drop
          attempt = 0;
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            let i: number;
            while ((i = buffer.indexOf("\n\n")) >= 0) { handleChunk(buffer.slice(0, i)); buffer = buffer.slice(i + 2); }
          }
        } catch { /* dropped: reconnect below */ }
        setConnected(false); liveState.connected = false;
        if (stopped) return;
        await new Promise((r) => setTimeout(r, Math.min(30_000, 1_000 * 2 ** Math.min(attempt, 5))));
        attempt++;
      }
    };

    void run();
    // Phones freeze background tabs; coming back to the front restarts the stream at once.
    const onVisible = () => { if (document.visibilityState === "visible" && !liveState.connected) { attempt = 0; controller?.abort(); } };
    document.addEventListener("visibilitychange", onVisible);
    return () => { stopped = true; controller?.abort(); document.removeEventListener("visibilitychange", onVisible); liveState.connected = false; };
  }, [enabled, dispatch]);

  return connected;
}
